import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { MdCloudUpload } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import css from "./DishLeftBlockCreateModal.module.css";
import DishProductsListModal from "../DishProductsListModal/DishProductsListModal";
import DishSemiFinishedList from "../DishSemiFinishedList/DishSemiFinishedList";
import { dishesService } from "../../../../services/dishes-services";
import { dishProductsAction, dishSemiFinishedActions } from "../../../../store/slices/dishes-slices";

const DishLeftBlockCreateModal = ({
  setDishProductAddOpen,
  setDishSemiFinishedAddOpen,
  setUploadedImage,
  initialImage,
  weight = null,
  weightNetto = null,
  dishId,
}) => {
  const { t } = useTranslation();
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const dispatch = useDispatch();
  const {
    setValue,
    formState: { errors },
    register,
  } = useFormContext();

  const { dishProductsWeight, dishProductsWeightNetto } = useSelector(
    (store) => store.dishProducts
  );
  const { dishSemiFinishedWeight, dishSemiFinishedWeightNetto } = useSelector(
    (store) => store.dishSemiFinished
  );
  const totalWeight = dishProductsWeight + dishSemiFinishedWeight;
  const totalWeightNetto =
    dishProductsWeightNetto + dishSemiFinishedWeightNetto;

  const [manualWeight, setManualWeight] = useState("0");
  const [nettoWeight, setNettoWeight] = useState("0");

  // 1. Ініціалізація фото
  useEffect(() => {
    if (initialImage) {
      setPreviewImageUrl(initialImage);
    }
  }, [initialImage]);

  // 3. Сетимо загальну вагу в стейт + форму
  useEffect(() => {
    if (weight) {
      const value = weight.toString();
      setManualWeight(value);
      setValue("weight", Number(value));
    }
  }, [weight, setValue]);

  // 4. Сетимо загальну вагу нетто в стейт + форму
  useEffect(() => {
    if (weightNetto) {
      const value = weightNetto.toString();
      setNettoWeight(value);
      setValue("weightNet", Number(value));
    }
  }, [weightNetto, setValue]);

  // sync weight and weightNetto
  const handleSyncWeight = () => {
    const value = totalWeight.toString();
    setManualWeight(value);
    setValue("weight", Number(value));
    dispatch(dishProductsAction.changeTrigger());
  };

  const handleSyncWeightNetto = () => {
    const value = totalWeightNetto.toString();
    setNettoWeight(value);
    setValue("weightNet", Number(value));
    dispatch(dishSemiFinishedActions.changeTrigger());
  };

  // 5. Upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadedImage(file);
    setPreviewImageUrl(URL.createObjectURL(file));
    event.target.value = null;
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setPreviewImageUrl(null);
  };

  const handleDeleteImageFromServer = async () => {
    if (!dishId) {
      toast.error(t("Cannot delete image: Dish ID not found"));
      return;
    }

    if (
      !window.confirm(
        t("Are you sure you want to delete this image from the server?")
      )
    ) {
      return;
    }

    try {
      await dishesService.deleteDishImage(dishId);
      setPreviewImageUrl(null);
      setUploadedImage(null);
      toast.success(t("Image deleted successfully!"));
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error(t("Failed to delete image"));
    }
  };

  // 6. Manual Weight
  const handleWeightChange = (e) => {
    const value = e.target.value;
    setManualWeight(value);
    setValue("weight", Number(value));
  };

  // 7. Netto Weight
  const handleNettoChange = (e) => {
    const value = e.target.value;
    setNettoWeight(value);
    setValue("weightNet", Number(value));
  };

  return (
    <div className={css.wrap}>
      <div className={css.uploadArea}>
        {previewImageUrl ? (
          <div className={css.previewContainer}>
            <img
              src={previewImageUrl}
              alt="preview"
              className={css.previewImage}
            />
            <button
              type="button"
              className={css.removeImageButton}
              onClick={handleRemoveImage}
              title={t("Remove preview")}
            >
              ×
            </button>
            {dishId && initialImage && (
              <button
                type="button"
                className={css.deleteImageButton}
                onClick={handleDeleteImageFromServer}
                title={t("Delete image from server")}
              >
                🗑️
              </button>
            )}
          </div>
        ) : (
          <label>
            <input
              type="file"
              accept=".jpg, .jpeg, .png"
              onChange={handleFileUpload}
            />
            <div>
              <MdCloudUpload className={css.iconUpload} />
            </div>
            <p className={css.textUpload}>
              {t("Drop-file-here-or")}{" "}
              <span className={css.textUploadLink}>{t("click-to-upload")}</span>
            </p>
          </label>
        )}
      </div>

      <div className={css.inputBlock}>
        <div className={css.inputGroup}>
          <label htmlFor="weight">{t("Weight")}</label>
          <input
            type="number"
            step="any"
            id="weight"
            value={manualWeight}
            onChange={handleWeightChange}
            className={errors.weight ? css.inputError : ""}
          />
          <button
            type="button"
            className={css.syncButton}
            onClick={handleSyncWeight}
          >
            Get total weight
          </button>
        </div>

        <div className={css.inputGroup}>
          <label htmlFor="weightNet">{t("weight-netto")}</label>
          <input
            type="number"
            step="any"
            id="weightNet"
            value={nettoWeight}
            onChange={handleNettoChange}
            className={errors.weightNet ? css.inputError : ""}
          />
          <button
            type="button"
            className={css.syncButton}
            onClick={handleSyncWeightNetto}
          >
            Get total weight netto
          </button>
        </div>

        <div className={css.inputGroup}>
          <label htmlFor="price">{t("modal-price")}</label>
          <input
            type="number"
            step="any"
            id="price"
            {...register("price")}
            className={errors.price ? css.inputError : ""}
          />
        </div>
      </div>

      <div className={css.productsContainer}>
        <div className={css.header}>
          <div className={css.product}>{t("settings-menu-title-products")}</div>
          <div className={css.weight}>{t("Weight")} (g)</div>
          <div className={css.weightNetto}>{t("weight-netto")} (g)</div>
          <div className={css.price}>{t("Cost")}</div>
          <div className={css.index}>{t("Index")}</div>
          <div className={css.add}>
            <button
              type="button"
              className={css.addButton}
              onClick={() => setDishProductAddOpen(true)}
            >
              +
            </button>
          </div>
        </div>
        <div className={css.listProducts}>
          <DishProductsListModal />
        </div>
      </div>

      <div className={css.productsContainer}>
        <div className={css.header}>
          <div className={css.product}>
            {t("aside-menu-item-semi-finished")}
          </div>
          <div className={css.weight}>{t("Weight")} (g)</div>
          <div className={css.weightNetto}>{t("weight-netto")} (g)</div>
          <div className={css.price}>{t("Cost")}</div>
          <div className={css.index}>{t("Index")}</div>
          <div className={css.add}>
            <button
              type="button"
              className={css.addButton}
              onClick={() => setDishSemiFinishedAddOpen(true)}
            >
              +
            </button>
          </div>
        </div>
        <div className={css.listProducts}>
          <DishSemiFinishedList />
        </div>
      </div>
    </div>
  );
};

export { DishLeftBlockCreateModal };
